import {
  ClientIntegrationType,
  useGetTicketingIntegrationsQuery,
  useLoadSpaceQuery,
  useProcessCaseEventsMutation,
} from "~/operations";
import { Space } from "~/lib/types";
import { Box } from "@mui/material";
import { TicketSettingsJiraIntegration } from "~/pages/tickets/components/TicketSettings/TicketSettingsJiraIntegration";
import { LoadingButton } from "~/components/loading-button";
import { Refresh } from "@mui/icons-material";
import { Flex } from "~/components/Flex";
import { useSnackbar } from "notistack";
import { TicketSettingsGeneral } from "~/pages/tickets/components/TicketSettings/TicketSettingsGeneral";
import { TicketSettingsIntegrationBox } from "~/pages/tickets/components/TicketSettings/TicketSettingsIntegrationBox";
import { TicketSettingsEmailIntegration } from "~/pages/tickets/components/TicketSettings/TicketSettingsEmailIntegration";
import { TicketSettingsZendeskIntegration } from "~/pages/tickets/components/TicketSettings/TicketSettingsZendeskIntegration";
import { TicketSettingsGithubIntegration } from "./TicketSettingsGithubIntegration";
import { TicketSettingsGitlabIntegration } from "~/pages/tickets/components/TicketSettings/TicketSettingsGitlabIntegration";
import { TicketSettingsAzureIntegration } from "~/pages/tickets/components/TicketSettings/TicketSettingsAzureIntegration";
import { TicketSettingsServiceNowIntegration } from "~/pages/tickets/components/TicketSettings/TicketSettingsServiceNowIntegration";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { CreateTicketingIntegration } from "~/pages/tickets/components/CreateTicketingIntegration/CreateTicketingIntegration";
import { useCanList } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/List";
import { TICKET_SYSTEM_INTEGRATIONS } from "~/pages/integrations/hosted-integrations";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { integrationDetailHref } from "~/pages/integrations/utils/integrationLinksUtils";

type CaseSettingsProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
};

export const TicketSettings = ({ space, scope }: CaseSettingsProps) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useLoadSpaceQuery({ variables: { spaceMrn: space.mrn } });

  const isAutoCreateEnabled =
    data?.space?.settings?.casesConfiguration.autoCreate;

  const canListIntegrations = useCanList(space);

  const { data: ticketingIntegrationsData } = useGetTicketingIntegrationsQuery({
    variables: {
      input: {
        spaceMrn: space.mrn,
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !canListIntegrations,
  });

  const [processCaseEvents, { loading: processCaseEventsLoading }] =
    useProcessCaseEventsMutation();

  const integrations = (
    ticketingIntegrationsData?.ticketingIntegrations?.integrations || []
  ).flatMap((a) => a ?? []);

  const handleProcessClick = async () => {
    if (!isFeatureEnabled("Developer Tools")) return;

    await processCaseEvents({
      variables: {
        input: {
          scopeMrn: space.mrn,
        },
      },
    });
    enqueueSnackbar("Successfully processed ticket events.", {
      variant: "success",
    });
  };

  if (integrations.length === 0) {
    return <CreateTicketingIntegration scope={scope} />;
  }

  return (
    <Box>
      {isFeatureEnabled("Developer Tools") && (
        <Flex justifyContent="flex-end">
          <LoadingButton
            loading={processCaseEventsLoading}
            buttonText="Process Ticket Events"
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={handleProcessClick}
            disabled={processCaseEventsLoading}
            data-name="danger-zone-delete-button"
            sx={{ mb: 4 }}
          />
        </Flex>
      )}

      <Box mb={3}>
        <TicketSettingsGeneral space={space} />
      </Box>

      <Flex flexDirection="column" gap={3}>
        {TICKET_SYSTEM_INTEGRATIONS.map((type) => {
          const integrationsByType = integrations.filter(
            (i) => i.type === type,
          );
          return (
            <Flex flexDirection="column" gap={3}>
              {integrationsByType.map((integration) => {
                const { type, mrn } = integration;
                const href = integrationDetailHref(type, mrn, space);
                return (
                  <TicketSettingsIntegrationBox
                    integrationName={integration.name}
                    href={href}
                    key={integration.mrn}
                  >
                    {integration.type ===
                      ClientIntegrationType.TicketSystemJira && (
                      <TicketSettingsJiraIntegration
                        key={integration.mrn}
                        integration={integration}
                        space={space}
                        showAutoCreateSection={Boolean(isAutoCreateEnabled)}
                      />
                    )}
                    {integration.type ===
                      ClientIntegrationType.TicketSystemEmail &&
                      integration?.configurationOptions?.__typename ===
                        "EmailConfigurationOptions" && (
                        <TicketSettingsEmailIntegration
                          key={integration.mrn}
                          isRecipientsEmpty={
                            integration?.configurationOptions?.recipients
                              ?.length === 0
                          }
                          recipients={
                            integration?.configurationOptions?.recipients
                          }
                          integration={integration}
                          space={space}
                          showAutoCreateSection={Boolean(isAutoCreateEnabled)}
                        />
                      )}
                    {integration.type ===
                      ClientIntegrationType.TicketSystemZendesk && (
                      <TicketSettingsZendeskIntegration
                        key={integration.mrn}
                        integration={integration}
                        space={space}
                        showAutoCreateSection={Boolean(isAutoCreateEnabled)}
                      />
                    )}
                    {integration.type ===
                      ClientIntegrationType.TicketSystemGithub && (
                      <TicketSettingsGithubIntegration
                        key={integration.mrn}
                        integration={integration}
                        space={space}
                        showAutoCreateSection={Boolean(isAutoCreateEnabled)}
                      />
                    )}
                    {integration.type ===
                      ClientIntegrationType.TicketSystemGitlab && (
                      <TicketSettingsGitlabIntegration
                        key={integration.mrn}
                        integration={integration}
                        space={space}
                        showAutoCreateSection={Boolean(isAutoCreateEnabled)}
                      />
                    )}
                    {integration.type ===
                      ClientIntegrationType.TicketSystemAzureDevops && (
                      <TicketSettingsAzureIntegration
                        key={integration.mrn}
                        integration={integration}
                        space={space}
                        showAutoCreateSection={Boolean(isAutoCreateEnabled)}
                      />
                    )}
                    {integration.type ===
                      ClientIntegrationType.TicketSystemServiceNow && (
                      <TicketSettingsServiceNowIntegration
                        key={integration.mrn}
                        integration={integration}
                        space={space}
                        showAutoCreateSection={Boolean(isAutoCreateEnabled)}
                      />
                    )}
                  </TicketSettingsIntegrationBox>
                );
              })}
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};
